import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { alpha, Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect, useRef, useState } from "react";
import Confetti from "react-confetti";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import { CheckMark } from "@/ui/Animations/Checkmark";
import { drawHalloweenTreat, HALLOWEEN_TREAT_COLORS } from "@/ui/Seasonal/halloweenTreats";
import { PumpkinPop } from "@/ui/Seasonal/PumpkinPop";
import SystemButton from "@/ui/SystemButton/SystemButton";
import type { NotificationResponse, ShiftNotificationDetails } from "@/utils/models";

const CONFETTI_PIECES = 200;
const CONFETTI_GRAVITY = 0.3;
const CONFETTI_DURATION_MS = 4_000;
// Treats are bigger than confetti, so fewer of them fill the screen as much.
const TREAT_PIECES = 110;
const APPROVAL_TYPE_LABEL_KEY: Record<string, string> = {
	approved: "pickup.approval.modal.type.openShift",
	approved_replace: "pickup.approval.modal.type.replace",
};

type Props = {
	open: boolean;
	approvals: NotificationResponse[];
	onClose: () => void;
};

type ShiftDisplay = {
	date: string | null;
	timeRange: string | null;
	location: string | null;
};

// Pulls structured fields off `parsed_data`. Falls back to message text when shift_date is missing.
const extractShiftDisplay = (parsed: Partial<ShiftNotificationDetails> | null | undefined): ShiftDisplay => {
	const date = parsed?.shift_date;
	const dateLabel = date && typeof date !== "string" && date.isValid ? date.toFormat("ccc, MMM d") : null;
	// BE writes shift_start / shift_end as preformatted "%I:%M %p" strings even though the TS type is DateTime.
	const start = parsed?.shift_start as unknown as string | undefined;
	const end = parsed?.shift_end as unknown as string | undefined;
	const timeRange = typeof start === "string" && typeof end === "string" ? `${start} – ${end}` : null;
	return { date: dateLabel, timeRange, location: parsed?.location ?? null };
};

export const ApprovalCelebrationModal: FC<Props> = ({ open, approvals, onClose }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const [showConfetti, setShowConfetti] = useState(false);
	const isHalloweenSeason = useIsHalloweenSeason();
	const theme = useTheme();
	const hasTrackedRef = useRef(false);

	useEffect(() => {
		if (!open) {
			hasTrackedRef.current = false;
			setShowConfetti(false);
			return;
		}
		if (hasTrackedRef.current) return;
		hasTrackedRef.current = true;
		track("approval_celebration_modal_shown", { request_count: approvals.length });
		setShowConfetti(true);
		const timeoutId = window.setTimeout(() => setShowConfetti(false), CONFETTI_DURATION_MS);
		return () => window.clearTimeout(timeoutId);
	}, [open, approvals.length, track]);

	if (approvals.length === 0) {
		return null;
	}

	return (
		<>
			{showConfetti &&
				(isHalloweenSeason ? (
					// Treats fall over the dialog instead of dimmed behind its backdrop; the canvas ignores the pointer, so "Got it" still works.
					<Confetti
						recycle={false}
						numberOfPieces={TREAT_PIECES}
						gravity={CONFETTI_GRAVITY}
						colors={HALLOWEEN_TREAT_COLORS}
						drawShape={drawHalloweenTreat}
						style={{ position: "fixed", zIndex: theme.zIndex.modal + 1 }}
					/>
				) : (
					<Confetti recycle={false} numberOfPieces={CONFETTI_PIECES} gravity={CONFETTI_GRAVITY} />
				))}
			<Dialog
				data-testid="approval-celebration-dialog"
				open={open}
				onClose={onClose}
				maxWidth="sm"
				fullWidth
				slotProps={{
					paper: { sx: { borderRadius: 4, p: 0.5, overflow: "hidden" } },
				}}
			>
				<DialogTitle sx={{ display: "flex", flexDirection: "column", alignItems: "center", pb: 2, pt: 3 }}>
					{isHalloweenSeason ? <PumpkinPop dataTestId="approval-celebration-pumpkin" /> : <CheckMark />}
					<Box
						component="span"
						sx={{ fontSize: { xs: "1.35rem", sm: "1.5rem" }, fontWeight: 800, color: "primary.dark", textAlign: "center", lineHeight: 1.2, mt: 1 }}
					>
						{t("pickup.approval.modal.title")}
					</Box>
				</DialogTitle>
				<DialogContent sx={{ pt: 3 }}>
					<Stack spacing={1.25}>
						{approvals.map((notification) => {
							const { date, timeRange, location } = extractShiftDisplay(notification.parsed_data);
							const typeLabel = t(APPROVAL_TYPE_LABEL_KEY[notification.notification_type] ?? "pickup.approval.modal.type.openShift");
							const headline = date && timeRange ? `${date} · ${timeRange}` : (date ?? timeRange ?? notification.message);
							return (
								<Box
									key={notification.id}
									data-testid={`approval-celebration-item-${notification.id}`}
									sx={(theme) => ({
										borderRadius: 2,
										backgroundColor: alpha(theme.palette.success.main, 0.08),
										p: 1.5,
										display: "flex",
										flexDirection: "column",
										gap: 0.5,
									})}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<CheckCircleRoundedIcon sx={{ fontSize: 16, color: "success.main" }} />
										<Typography variant="caption" sx={{ fontWeight: 600, color: "success.dark" }}>
											{typeLabel}
										</Typography>
									</Box>
									<Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "text.primary", lineHeight: 1.35 }}>{headline}</Typography>
									{location && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<LocationOnRoundedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
											<Typography variant="caption" sx={{ color: "text.secondary" }}>
												{location}
											</Typography>
										</Box>
									)}
								</Box>
							);
						})}
					</Stack>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", pb: 2.5, pt: 1 }}>
					<SystemButton
						onClick={onClose}
						buttonVariant="primary"
						data-testid="approval-celebration-confirm"
						sx={{ minWidth: 180, fontWeight: 700, py: 1.25, borderRadius: 999 }}
					>
						{t("pickup.approval.modal.confirm")}
					</SystemButton>
				</DialogActions>
			</Dialog>
		</>
	);
};
